import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import type { User } from "@shared/schema";
import { isFailedRenewal } from "@/lib/access";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

interface PlanPrice {
  amount: number; // cents
  currency: string; // "eur"
  interval: "month" | "year";
  taxBehavior: "exclusive" | "inclusive" | "unspecified";
}

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: currency.toUpperCase() }).format(amount / 100);
}

const INTERVAL_LABEL: Record<string, string> = { month: "Monat", year: "Jahr" };

// Poll cap (Phase 5 item 5): access is granted by the verified webhook, not the Checkout return. Normally
// it lands in a few seconds; if the webhook is delayed/lost the poll must NOT spin forever. After ~60s
// (24 × 2.5s) we stop and show a "still processing / contact support" fallback instead of an endless spinner.
const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 24;
const SUPPORT_EMAIL = "support@socialmedia-a-team.com";

// Payment step shown AFTER register/verify and BEFORE the onboarding wizard (ONBOARDING_PLAN.md item 26)
// for users whose access_state is pending_payment / expired. Access is granted by the verified Stripe
// webhook — NOT by the return from Checkout — so after a successful return we poll /api/auth/me until the
// backend reflects the new state, then the App gate routes onward automatically.
export default function PaymentStep() {
  const { toast } = useToast();
  const params = new URLSearchParams(window.location.search);
  const checkoutResult = params.get("checkout"); // "success" | "cancelled" | null
  const [waitingForAccess, setWaitingForAccess] = useState(checkoutResult === "success");
  const [pollExhausted, setPollExhausted] = useState(false);
  const { user } = useAuth();
  const account = user as User | undefined;
  // A failed renewal turns past_due into expired while Stripe still holds a live subscription: checkout then
  // answers 409 ALREADY_SUBSCRIBED and only the billing portal (new card) brings access back. When /api/auth/me
  // already says so (lapsed grace), open with the portal instead of the checkout.
  const [paymentFailed, setPaymentFailed] = useState(() => isFailedRenewal(account));
  const hasBillingAccount = account?.accessState === "expired" && (!!account?.stripeCustomerId || isFailedRenewal(account));

  const { data: price } = useQuery<PlanPrice>({ queryKey: ["/api/billing/price"], retry: false });

  // After a successful Checkout return, poll for the webhook to land (access reflects the webhook, not
  // this redirect). Each invalidate refetches /api/auth/me; once access_state flips, the App gate
  // unmounts this component. CAPPED at MAX_POLL_ATTEMPTS so a delayed/lost webhook can't spin forever —
  // we surface a support fallback instead.
  useEffect(() => {
    if (!waitingForAccess) return;
    let attempts = 0;
    queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    const id = setInterval(() => {
      attempts += 1;
      if (attempts >= MAX_POLL_ATTEMPTS) {
        clearInterval(id);
        setWaitingForAccess(false);
        setPollExhausted(true);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [waitingForAccess]);

  const retryPolling = () => {
    setPollExhausted(false);
    setWaitingForAccess(true);
  };

  const checkout = useMutation({
    mutationFn: () => apiRequest("POST", "/api/billing/checkout", {}),
    onSuccess: (res: { url?: string }) => {
      if (res?.url) {
        window.location.href = res.url; // hand off to Stripe-hosted Checkout
      } else {
        toast({ title: "Fehler", description: "Checkout-Link fehlt. Bitte erneut versuchen.", variant: "destructive" });
      }
    },
    onError: (err: any) => {
      // Already subscribed: a live subscription exists, so the last renewal most likely failed. Offer the
      // portal instead of waiting for a webhook that will not come.
      if (err?.data?.code === "ALREADY_SUBSCRIBED") {
        setPaymentFailed(true);
        return;
      }
      toast({ title: "Fehler", description: err?.data?.error || err?.message || "Bitte erneut versuchen.", variant: "destructive" });
    },
  });

  const portal = useMutation({
    mutationFn: () => apiRequest("POST", "/api/billing/portal", {}),
    onSuccess: (res: { url?: string }) => {
      if (res?.url) window.location.href = res.url;
      else toast({ title: "Fehler", description: "Der Link zum Kundenportal fehlt. Bitte versuchen Sie es erneut.", variant: "destructive" });
    },
    onError: (err: any) => {
      toast({ title: "Fehler", description: err?.data?.error || "Das Kundenportal ist gerade nicht erreichbar.", variant: "destructive" });
    },
  });

  const logout = async () => {
    try { await apiRequest("POST", "/api/auth/logout", {}); } catch { /* ignore */ }
    window.location.href = "/";
  };

  // Net prices (tax_behavior=exclusive) are shown as such; Stripe Tax adds VAT at checkout.
  const isNet = price?.taxBehavior === "exclusive";
  const priceLabel = price
    ? `${formatPrice(price.amount, price.currency)}${isNet ? " netto" : ""} pro ${INTERVAL_LABEL[price.interval] ?? "Monat"}`
    : "…";
  const vatNote = isNet ? "zzgl. USt." : "inkl. USt.";

  if (waitingForAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-green-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <Loader2 className="w-10 h-10 text-green-600 animate-spin mx-auto" />
            <h2 className="text-xl font-semibold text-slate-900">Zahlung erfolgreich!</h2>
            <p className="text-slate-600">Ihr Zugang wird aktiviert. Einen Moment bitte…</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-green-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">Ihre letzte Zahlung ist fehlgeschlagen</h2>
            <p className="text-slate-600">
              Bitte aktualisieren Sie Ihre Zahlungsmethode im Kundenportal. Danach wird Ihr Zugang automatisch
              wieder freigeschaltet.
            </p>
            <div className="space-y-2 pt-1">
              <Button className="w-full" size="lg" onClick={() => portal.mutate()} disabled={portal.isPending} data-testid="button-update-payment-method">
                {portal.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Zahlungsmethode aktualisieren"}
              </Button>
              <Button variant="outline" className="w-full" onClick={() => { setPaymentFailed(false); retryPolling(); }} data-testid="button-recheck-access">
                Erneut prüfen
              </Button>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="block text-sm text-slate-500 underline hover:text-slate-700"
              >
                Support kontaktieren ({SUPPORT_EMAIL})
              </a>
            </div>
            <div>
              <button onClick={logout} className="text-sm text-slate-400 hover:text-slate-600 underline">
                Abmelden
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (pollExhausted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-green-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <Loader2 className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">Ihre Zahlung wird noch verarbeitet</h2>
            <p className="text-slate-600">
              Ihre Zahlung ist eingegangen, aber die Aktivierung dauert länger als erwartet. Das löst sich
              meist von selbst. Bitte prüfen Sie es gleich erneut oder kontaktieren Sie uns, falls es bestehen bleibt.
            </p>
            <div className="space-y-2 pt-1">
              <Button className="w-full" size="lg" onClick={retryPolling} data-testid="button-retry-activation">
                Erneut prüfen
              </Button>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="block text-sm text-slate-500 underline hover:text-slate-700"
                data-testid="link-contact-support"
              >
                Support kontaktieren ({SUPPORT_EMAIL})
              </a>
            </div>
            <div>
              <button onClick={logout} className="text-sm text-slate-400 hover:text-slate-600 underline">
                Abmelden
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-green-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <ShieldCheck className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="text-2xl">Gründungsprogramm für Hotels</CardTitle>
          <CardDescription>Voller Zugang ab dem ersten Tag. Monatlich kündbar.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {checkoutResult === "cancelled" && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Zahlung abgebrochen. Sie können es jederzeit erneut starten.
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
            <ul className="space-y-1.5 text-sm text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" /> <span><strong>{priceLabel}</strong> {vatNote}</span></li>
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" /> Einrichtungsgebühr von 490 € entfällt</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" /> Monatlich kündbar im Kundenportal</li>
            </ul>
          </div>

          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900" data-testid="text-start-guarantee">
            <strong>Start-Garantie:</strong> Ist Ihr Social Media A-Team nicht innerhalb von 7 Tagen startklar, ist Ihr erster Monat kostenlos.
          </div>

          {hasBillingAccount && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-2" data-testid="text-billing-expired">
              <p>
                Ihr Abonnement ist nicht mehr aktiv. Falls Ihre letzte Zahlung fehlgeschlagen ist, aktualisieren
                Sie bitte Ihre Zahlungsmethode.
              </p>
              <Button variant="outline" size="sm" onClick={() => portal.mutate()} disabled={portal.isPending} data-testid="button-update-payment-method">
                {portal.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Zahlungsmethode aktualisieren"}
              </Button>
            </div>
          )}

          <Button
            className="w-full"
            size="lg"
            onClick={() => checkout.mutate()}
            disabled={checkout.isPending}
            data-testid="button-start-checkout"
          >
            {checkout.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Weiter zur Zahlung"}
          </Button>

          {/* DACH/EU compliance disclosure (item 27): auto-renewal, cancellation, receipts. */}
          <p className="text-xs leading-relaxed text-slate-500">
            Mit „Weiter zur Zahlung" gelangen Sie zu unserem Zahlungsdienstleister Stripe. Dort schließen
            Sie das Abonnement ab; der erste Monat wird sofort abgebucht ({priceLabel} {vatNote}).
            Das Abonnement verlängert sich automatisch um jeweils einen Monat und kann jederzeit zum Ende
            des laufenden Abrechnungsmonats über das Kundenportal gekündigt werden. Rechnungen erhalten Sie
            per E-Mail. Es gelten unsere{" "}
            <a href="/agb" className="underline hover:text-slate-700">AGB</a>, unsere{" "}
            <a href="/datenschutz" className="underline hover:text-slate-700">Datenschutzerklärung</a> und unser{" "}
            <a href="/impressum" className="underline hover:text-slate-700">Impressum</a>.
          </p>

          <div className="text-center">
            <button onClick={logout} className="text-sm text-slate-400 hover:text-slate-600 underline">
              Abmelden
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
